import { twMerge } from "tailwind-merge";
import {
  Anchor,
  Button,
  Card,
  CardContent,
  CardHeader,
  H2,
  H5,
  H6,
} from "@sellar-io/components";
import { formatCurrency } from "@sellar-io/common";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router";
import { useMediaQuery } from "usehooks-ts";
import { DiscountSummary, Icon, SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import {
  BestPricePromise,
  BestPricePromiseFullSize,
  BestPricePromiseCompact,
} from "../../../components/bestPricePromise/BestPricePromise";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { PriceTierOrderRuleSummaryLegacy } from "../../../components/priceTierOrderRuleSummary";
import * as ROUTES from "../../../constants/routes";
import {
  PRICE_TIER,
  PRICE_TIER_EDIT,
  PRICE_TIERS,
} from "../../../constants/routes";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { usePriceTier } from "../../../hooks/api/v2/priceTier/usePriceTier";
import { usePriceTierDelete } from "../../../hooks/api/v2/priceTier/usePriceTierDelete";
import { useProductList } from "../../../hooks/api/v2/product/useProductList";
import { useParamAsNumber } from "../../../hooks/useParamAsNumber";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { PriceTierDuplicateDialog } from "./dialogs/PriceTierDuplicateDialog";
import { PriceTierItem } from "./PriceTierItem";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { usePriceTierGetStats } from "../../../hooks/api/v2/priceTier/usePriceTierGetStats";
import { usePriceTierList } from "../../../hooks/api/v2/priceTier/usePriceTierList";
import { useForm } from "react-hook-form";
import {
  PriceTierGetStatsResponseType,
  PriceTierListResponseType,
} from "@sellar-io/api-v2/types";
import { InputLabel, InputContainer, InputSelect } from "@sellar-io/components";
import { usePriceTierDeleteAndReassign } from "../../../hooks/api/v2/priceTier/usePriceTierDeleteAndReassign";
import { AlertCompact } from "../../../components/alert/AlertCompact";

export function PriceTier(): ReactElement {
  const navigate = useNavigate();
  const priceTierId = useParamAsNumber(PRICE_TIER, "priceTierId");
  const priceTierQuery = usePriceTier(priceTierId);
  const priceTierDelete = usePriceTierDelete(priceTierId);
  const productsList = useProductList({
    query: [
      { type: "PriceTierId", modifier: "EQUALS", value: priceTierId },
      { type: "type", modifier: "IN", value: ["productItem", "badge"] },
      { type: "active", modifier: "EQUALS", value: true },
    ],
    sortBy: "name",
    sortDir: "asc",
    limit: 50,
  });

  const unbundlingRelease2aEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2A",
  );

  const { business } = useSellarCurrentBusiness();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [deleteDialogActive, setDeleteDialogActive] = useState(false);
  const [duplicateDialogActive, setDuplicateDialogActive] = useState(false);
  const priceTierDiscounts =
    priceTierQuery.data?.VolumeDiscounts?.PriceTierDiscounts;
  const showStackedDiscountWarning = Boolean(
    priceTierDiscounts?.some((discount) => discount.itemType === null) &&
    priceTierDiscounts.some((discount) => discount.itemType !== null),
  );
  const openBestPricePromiseInfo = () =>
    window.open("https://sellar.io/supplier-house-rules", "_blank");
  const openDiscountSupplierGuide = () =>
    window.open(
      "https://intercom.help/sellario/en/articles/9947078-supplier-guide-discounts",
      "_blank",
    );

  return (
    <>
      <NavigationBar
        title={!priceTierQuery.data ? "Loading..." : priceTierQuery.data.name}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => navigate(ROUTES.PRICE_TIERS)}
        rightComponent={
          <Button
            size={"small"}
            onClick={() => navigate(PRICE_TIER_EDIT.href({ priceTierId }))}
          >
            <Icon icon={"PENCIL"} /> Edit
          </Button>
        }
      />
      {priceTierQuery.isPending ? (
        <SpinnerLegacy />
      ) : priceTierQuery.isError ? (
        <div>Something went wrong</div>
      ) : (
        <>
          <div className="mx-auto flex max-w-[900px] flex-col gap-2 p-2">
            <Card className="bg-white">
              <CardHeader
                className={twMerge(
                  "bg-white",
                  "flex items-center justify-between p-4",
                )}
              >
                <div className="flex flex-col">
                  <H5>Price tier properties</H5>
                  <H6>Review and/ or edit this price tier's properties</H6>
                </div>
                <div className="flex flex-row gap-2">
                  <Button
                    className={"justify-end"}
                    size={"small"}
                    color={"secondary"}
                    onClick={() => setDuplicateDialogActive(true)}
                  >
                    <Icon icon={"PLUS_BOX_MULTIPLE_OUTLINE"} />
                    Duplicate
                  </Button>
                  <PriceTierDeleteButton
                    priceTierName={priceTierQuery.data.name}
                    priceTierId={priceTierId}
                  />
                </div>
              </CardHeader>
              <CardContent className={"flex flex-col gap-4 p-4"}>
                {priceTierQuery.data.VolumeDiscounts?.PriceTierDiscounts &&
                priceTierQuery.data.VolumeDiscounts.PriceTierDiscounts.length >
                  0 ? (
                  <>
                    {showStackedDiscountWarning && (
                      <Alert
                        type="warning"
                        title="Warning - 'any' item volume discounts stack with container-specific volume discounts"
                        message="If your customer's order qualifies for both an 'any'
                        item volume discount and a container-specific volume
                        discount, both those discounts will be applied to their
                        order. Read the last section of the help guide linked
                        below."
                      >
                        <Button
                          color={"secondary"}
                          className={
                            "border-orange-300 bg-orange-100 text-orange-800"
                          }
                          onClick={() => openDiscountSupplierGuide()}
                        >
                          Learn more
                        </Button>
                      </Alert>
                    )}
                    <DiscountSummary
                      discounts={
                        priceTierQuery.data.VolumeDiscounts.PriceTierDiscounts
                      }
                      currency={business?.Supplier?.currency}
                    />
                  </>
                ) : (
                  <p>No discounts</p>
                )}
                {!unbundlingRelease2aEnabled && (
                  <>
                    {priceTierQuery.data.minimumOrderPrice ? (
                      <div className="">
                        <div className="">Minimum order size</div>
                        {formatCurrency(
                          priceTierQuery.data.minimumOrderPrice,
                          business?.Supplier?.currency,
                        )}
                      </div>
                    ) : (
                      <p>No minimum order price</p>
                    )}

                    {priceTierQuery.data.OrderRules?.PriceTierOrderRules &&
                    priceTierQuery.data.OrderRules.PriceTierOrderRules.length >
                      0 ? (
                      <PriceTierOrderRuleSummaryLegacy
                        priceTierOrderRules={priceTierQuery.data.OrderRules.PriceTierOrderRules.map(
                          (rule) => ({
                            ...rule,
                            id: parseInt(rule.id.toString()),
                          }),
                        )}
                      />
                    ) : (
                      <p>No minimum order quantities</p>
                    )}
                  </>
                )}
                {unbundlingRelease2aEnabled && (
                  <AlertCompact
                    type="information"
                    className="text-sm"
                    onClick={() => navigate(ROUTES.ORDER_RULES)}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <div className="flex items-center gap-1 font-semibold">
                        <span>
                          Minimum order rules have been temporarily moved.
                        </span>
                        <Anchor
                          className="inline-flex items-center gap-1 text-blue-600 underline"
                          href="/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Find out why
                          <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
                        </Anchor>
                      </div>
                      <span className="flex items-center gap-2 font-semibold text-blue-800">
                        Go to Order rules
                        <Icon icon="CHEVRON_RIGHT" />
                      </span>
                    </div>
                  </AlertCompact>
                )}
              </CardContent>
            </Card>

            <Alert
              type="information"
              title="Looking to hide a product from your price tier?"
              message={
                <p>
                  You can now control product visibility independently from your
                  price tiers using your product catalogues.{" "}
                  <Anchor
                    className="inline-flex items-center gap-1 text-blue-600 underline"
                    href="https://intercom.help/sellario/en/articles/12008469-supplier-guide-product-catalogues"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn more about your product catalogues here
                    <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
                  </Anchor>
                </p>
              }
            >
              <Button
                color="secondary"
                className="mt-4 border-blue-800 bg-transparent text-blue-800 hover:bg-blue-200"
                onClick={() => navigate(ROUTES.AVAILABILITY_TIERS)}
              >
                Go to product catalogues
              </Button>
            </Alert>

            <Card className="bg-white">
              <CardHeader
                className={twMerge(
                  "bg-white",
                  "flex flex-row items-center justify-between gap-2 p-4",
                )}
              >
                <div className="flex flex-col">
                  <H5>Product pricing</H5>
                  <H6>
                    Please consider{" "}
                    <span
                      color={"tertiary"}
                      className={"cursor-pointer text-blue-800 underline"}
                      onClick={openBestPricePromiseInfo}
                    >
                      The `Best Price Promise` Rule
                    </span>{" "}
                    when pricing your products
                  </H6>
                </div>
                <BestPricePromise onClick={openBestPricePromiseInfo}>
                  {isMobile ? (
                    <BestPricePromiseCompact />
                  ) : (
                    <BestPricePromiseFullSize />
                  )}
                </BestPricePromise>
              </CardHeader>
              <CardContent className={"p-4"}>
                <InfiniteList query={productsList}>
                  {productsList.isLoading ? (
                    <SpinnerLegacy />
                  ) : (
                    productsList.data?.pages.map((page) =>
                      page.map((product) => (
                        <div className="section" key={product.id}>
                          <H2 className={"my-5"}>{product.name}</H2>
                          <PriceTierItem key={product.id} product={product} />
                        </div>
                      )),
                    )
                  )}
                </InfiniteList>
              </CardContent>
            </Card>
          </div>
          <PriceTierDuplicateDialog
            active={duplicateDialogActive}
            onDismiss={() => setDuplicateDialogActive(false)}
            priceTierName={priceTierQuery.data.name}
            priceTierId={priceTierId}
          />
          <Dialog
            active={deleteDialogActive}
            onDismiss={() => setDeleteDialogActive(false)}
          >
            <DialogNavigation
              leftIcon={"CHEVRON_DOWN"}
              onClickLeftIcon={() => setDeleteDialogActive(false)}
              title={`Delete ${priceTierQuery.data.name}?`}
              description={`Are you sure you want to delete the ${priceTierQuery.data.name} price tier?`}
            />
            <DialogContent>
              {priceTierDelete.isError && (
                <Alert
                  type={"error"}
                  message={
                    apiV2AssertError(priceTierDelete.error)?.response?.data
                      .message ?? "Unknown error occurred"
                  }
                />
              )}
            </DialogContent>
            <DialogCTA>
              <Button
                color={"red"}
                loading={priceTierDelete.isPending}
                onClick={() =>
                  priceTierDelete.mutate(undefined, {
                    onSuccess: () => {
                      navigate(PRICE_TIERS);
                    },
                  })
                }
              >
                Delete
              </Button>
            </DialogCTA>
          </Dialog>
        </>
      )}
    </>
  );
}
type PriceTierDeleteButtonProps = {
  priceTierName: string;
  priceTierId: number;
};
const PriceTierDeleteButton = ({
  priceTierName,
  priceTierId,
}: PriceTierDeleteButtonProps) => {
  const [activeDialog, setActiveDialog] = useState(false);
  const navigate = useNavigate();
  const priceTierStatsQuery = usePriceTierGetStats(priceTierId, {
    enabled: Boolean(priceTierId),
  });
  const priceTierListQuery = usePriceTierList(
    {},
    {
      enabled: Boolean(priceTierId),
    },
  );
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState("");
  const priceTierDeleteAndReassignMutation =
    usePriceTierDeleteAndReassign(priceTierId);

  const handleDeleteAndReassign = (
    data: PriceTierDeleteAndReassignFormValues,
  ) => {
    priceTierDeleteAndReassignMutation.mutate(
      {
        customerZones: data.customerZones.map((customerZone) => ({
          id: customerZone.customerZoneId,
          newPriceTierId: customerZone.newPriceTierId,
        })),
        directlyAssignedCustomers:
          data.newPriceTierIdForDirectlyAssignedCustomers
            ? {
                oldPriceTierId: priceTierId,
                newPriceTierId: data.newPriceTierIdForDirectlyAssignedCustomers,
              }
            : null,
        transferDiscountable: false,
      },
      {
        onSuccess: () => {
          setActiveDialog(false);
          navigate(ROUTES.PRICE_TIERS);
        },
        onError: (error) => {
          const message = apiV2AssertError(error)?.response?.data.message ?? "";
          setSubmissionErrorMessage(message);
        },
      },
    );
  };

  const customersDirectlyAssignedCount =
    priceTierStatsQuery.data?.customersDirectlyAssignedCount ?? 0;
  const customerZonesAssigned =
    priceTierStatsQuery.data?.customerZonesAssigned ?? [];

  const priceTierList = priceTierListQuery.data?.pages[0] ?? [];

  const hasEntitiesToReassign =
    customersDirectlyAssignedCount > 0 || customerZonesAssigned.length > 0;

  const description = hasEntitiesToReassign
    ? "To delete this price tier, you will need to re-assign a different price tier to the individual customers and/or customer zones that currently have it assigned"
    : `Are you sure you want to delete ${priceTierName} price tier`;

  const isSubmitting = priceTierDeleteAndReassignMutation.isPending;

  return (
    <>
      <Button
        className="justify-end"
        size="small"
        color="red"
        onClick={() => setActiveDialog(true)}
      >
        <Icon icon={"DELETE"} />
        Delete
      </Button>
      <Dialog active={activeDialog} onDismiss={() => setActiveDialog(false)}>
        <DialogNavigation
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={() => setActiveDialog(false)}
          title={`Delete ${priceTierName}?`}
          description={description}
        />
        <DialogContent>
          {hasEntitiesToReassign ? (
            <PriceTierDeleteAndReassignForm
              customerZonesAssigned={customerZonesAssigned}
              customersDirectlyAssignedCount={customersDirectlyAssignedCount}
              priceTierList={priceTierList}
              currentPriceTierId={priceTierId}
              isSubmitting={priceTierDeleteAndReassignMutation.isPending}
              errorMessage={submissionErrorMessage}
              onSubmit={handleDeleteAndReassign}
            />
          ) : (
            <div className="flex justify-end">
              <Button
                color="red"
                disabled={isSubmitting}
                loading={isSubmitting}
                onClick={() => {
                  handleDeleteAndReassign({
                    customerZones: [],
                    newPriceTierIdForDirectlyAssignedCustomers: null,
                    transferDiscountable: false,
                  });
                }}
              >
                Delete
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

type PriceTierDeleteAndReassignFormProps = {
  priceTierList: PriceTierListResponseType;
  currentPriceTierId: number;
  customersDirectlyAssignedCount: number;
  customerZonesAssigned: PriceTierGetStatsResponseType["customerZonesAssigned"];
  isSubmitting: boolean;
  errorMessage: string;
  onSubmit: (_data: PriceTierDeleteAndReassignFormValues) => void;
};
type PriceTierDeleteAndReassignFormValues = {
  customerZones: Array<{ customerZoneId: number; newPriceTierId: number }>;
  newPriceTierIdForDirectlyAssignedCustomers: number | null;
  transferDiscountable: boolean;
};

const PriceTierDeleteAndReassignForm = ({
  priceTierList,
  currentPriceTierId,
  customersDirectlyAssignedCount,
  customerZonesAssigned,
  errorMessage,
  isSubmitting,
  onSubmit,
}: PriceTierDeleteAndReassignFormProps) => {
  const form = useForm<PriceTierDeleteAndReassignFormValues>({
    defaultValues: {
      customerZones: [],
      transferDiscountable: false,
    },
  });

  const filteredPriceTierList = priceTierList.filter(
    (priceTier) => priceTier.id !== currentPriceTierId,
  );

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
    >
      {errorMessage && (
        <Alert type="error" message={errorMessage} className="w-full" />
      )}
      <div className="flex flex-col gap-4">
        {customerZonesAssigned.map((customerZoneAssigned, idx) => {
          const customerZoneName =
            customerZoneAssigned.name ??
            `Customer Zone - ${customerZoneAssigned.id}`;
          return (
            <div key={customerZoneAssigned.id} className="flex flex-col gap-2">
              <InputLabel>{`Select price tier for ${customerZoneAssigned.customersAssignedCount} customers in '${customerZoneName}' customer zone`}</InputLabel>
              <InputContainer>
                <InputSelect
                  {...form.register(`customerZones.${idx}`, {
                    required: true,
                    setValueAs: (value) => ({
                      customerZoneId: customerZoneAssigned.id,
                      newPriceTierId: Number(value),
                    }),
                  })}
                >
                  {filteredPriceTierList.map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.name}
                    </option>
                  ))}
                </InputSelect>
              </InputContainer>
            </div>
          );
        })}
        <div className="flex flex-col gap-2">
          <InputLabel>{`Select price tier for ${customersDirectlyAssignedCount} individually assigned customers`}</InputLabel>
          <InputContainer>
            <InputSelect
              {...form.register("newPriceTierIdForDirectlyAssignedCustomers", {
                required: true,
                setValueAs: (value) => Number(value),
              })}
            >
              {filteredPriceTierList.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name}
                </option>
              ))}
            </InputSelect>
          </InputContainer>
        </div>
      </div>
      <div className="flex justify-end">
        <Button disabled={isSubmitting} loading={isSubmitting} type="submit">
          Delete and Assign
        </Button>
      </div>
    </form>
  );
};
